import { Injectable } from '@angular/core';
import { BankAccountViewModel } from '@models/bank-account.class';
import { ConfirmationOrder } from '@models/confirmation-order';
import { DayTradesViewModel } from '@models/day-trades.class';
import { OperationError } from '@models/operationError.class';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { isEmpty } from 'lodash';
import {
  combineLatest,
  EMPTY,
  Observable,
  of as observableOf,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { buyOrderTypes } from 'src/app';
import { AppState } from 'src/app/state/app-state';
import { DAY_TRADES, IVA } from 'src/app/state/app.reducer';
import { selectContractsAccountsAccounts } from 'src/app/state/selectors/contract-accounts-selectors';
import { selectContractId } from 'src/app/state/selectors/current-contract.selectors';
import { NotificationService } from 'src/core/services/notification.service';
import { ApiGatewayService } from 'src/shared';
import { WithdrawalAmountResponse } from '../models/withdrawal.class';
import { IDayTradesResponse } from '../responses/day-trades.response';
import { ITransactionsResponse } from '../responses/transactions.response';
@Injectable()
export class OperationService {
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;
  private loremAPI: string;
  private gbmComAPI: string;
  private tradingUsaAPI: string;
  private BUSINESS_PARTY = 'CASA_DE_BOLSA';
  private operationError: OperationError;

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private apiGatewayService: ApiGatewayService,
  ) {
    this.appConfiguration$ = this.store.select('config');
    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
      )
      .subscribe((value: any) => {
        this.gbmAPI = value.api;
        this.gbmComAPI = value.gbmComApi;
        this.loremAPI = value.loremApi;
        this.tradingUsaAPI = value.tradingUsaApi;
      });
  }

  getIVA(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Operation/GetIVA`)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
        tap((result) => {
          this.store.dispatch({
            type: IVA,
            payload: +result.response,
          });
        }),
        map(({ response }) => response),
      );
  }

  getContractProperties(
    contractId: string,
    isTimer?: boolean,
    isRealTime?: boolean,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetContractProperties`,
      { contractId, isRealTime },
      isTimer,
    );
  }

  cancelOrder(
    orderId: string,
    predispatcher: boolean,
    validity: any,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/CancelOrder`,
      {
        ElectronicOrderId: orderId,
        IsPredispatchOrder: predispatcher,
        vigencia: validity,
      },
    );
  }

  registerWithDrawal(data: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Operation/RegisterWithdrawal`, data)
      .pipe(
        catchError((error: any) => {
          return observableThrowError(error);
        }),
      );
  }

  getAvailableFunds(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Operation/GetAvailableFundsForTrade/true`)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  registerCapitalOrder(data: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Operation/RegisterCapitalOrder`, data)
      .pipe(
        catchError((error: any) => {
          this.operationError = new OperationError(
            error,
            this.isBuyByCapitalOrderType(data?.orders[0]),
            data?.orders[0]?.instrumentType,
          );

          this.notificationService.showServiceError(this.operationError);
          return observableThrowError(this.operationError);
        }),
      );
  }

  registerUsaOrder(
    contractId: string,
    accountId: string,
    data: ConfirmationOrder,
  ): Observable<any> {
    let order: Record<string, any> = {
      side: data.isBuy ? 'buy' : 'sell',
      order_type: 'market',
      instrument_id: data.instrumentId,
      security_id: data.issueID,
    };
    if (data.sellAllPosition) {
      order = { ...order, sell_all_position: true };
    } else {
      order = { ...order, amount: data.total };
    }

    return this.apiGatewayService
      .post(
        `${this.tradingUsaAPI}v1/orders/contracts/${contractId}/accounts/${accountId}/orders`,
        order,
      )
      .pipe(
        catchError((error: OperationError) => {
          this.notificationService.showServiceError(error);
          return Observable.throw(error);
        }),
      );
  }

  getBlotterUsa(isTimer?: boolean): Observable<any> {
    return this.getCurrentComContractAndAccount().pipe(
      switchMap(({ accountId, contractId }) =>
        this.apiGatewayService.get(
          `${this.tradingUsaAPI}v1/orders/contracts/${contractId}/accounts/${accountId}/orders`,
          isTimer,
        ),
      ),
    );
  }

  getCapitalMarketContractRisk(
    contractId: string,
    isTimer?: boolean,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetCapitalMarketContractRisk`,
      { contractId },
      isTimer,
    );
  }

  cancelUsaOrder(orderId: string) {
    return this.getCurrentComContractAndAccount().pipe(
      switchMap(({ accountId, contractId }) =>
        this.apiGatewayService.put(
          `${this.tradingUsaAPI}v1/orders/contracts/${contractId}/accounts/${accountId}/orders/${orderId}/cancel`,
        ),
      ),
    );
  }

  getBlotterCapitalMarket(data: any, isTimer?: boolean): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetBlotterOrders`,
      data,
      isTimer,
    );
  }

  registerFundsOrder(data: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Operation/RegisterFundsOrder`, data)
      .pipe(
        catchError((error: any) => {
          this.operationError = new OperationError(error);
          this.notificationService.showServiceError(this.operationError);
          return observableThrowError(this.operationError);
        }),
      );
  }

  GetAllAvailableFunds(personalityType: number): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Operation/GetAllAvailableFunds`, {
        request: personalityType,
      })
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getExternalFunds(legalPersonality: number): Observable<any> {
    return this.apiGatewayService
      .get(
        `${this.gbmAPI}v1/external-issue-funds?legal_personality=${legalPersonality}`,
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getFundsURL(): Observable<any> {
    const version = new Date().getTime();
    return this.apiGatewayService
      .get(`/assets/gbm-fondos.json?v=${version}`)
      .pipe(
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  getHistoricalCashOperations(info: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetHistoricalCashOperations`,
      info,
    );
  }

  getMovements(
    contractId: string,
    accountsId: string,
    pageNumber: number,
    pageSize: number,
    orderType: string,
    startDate: string,
    endDate: string,
  ) {
    const orderTypeFilter = orderType ? `&order_type=${orderType}` : '';
    const timeFilter =
      startDate && endDate
        ? `&start_date=${startDate}&end_date=${endDate}`
        : '';
    const url = `${this.gbmComAPI}v1/contracts/${contractId}/accounts/${accountsId}/cash-transactions?page=${pageNumber}&page_size=${pageSize}${orderTypeFilter}${timeFilter}`;

    return this.apiGatewayService.get(url);
  }

  getUsaCapitalMovements(
    pageNumber: number,
    pageSize: number,
    startDate: string,
    endDate: string,
  ): Observable<ITransactionsResponse> {
    const params = `?page=${pageNumber}&page_size=${pageSize}&start_date=${startDate}&end_date=${endDate}`;

    return this.getCurrentComContractAndAccount().pipe(
      switchMap(({ accountId }) => {
        return this.apiGatewayService.get(
          `${this.tradingUsaAPI}v1/accounts/${accountId}/transactions${params}`,
        );
      }),
    );
  }

  getIntradayCashOperations(info: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetIntradayCashOperations`,
      info,
    );
  }

  getContractTopCashMovements(contractId: string): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Operation/GetContractTopCashMovements`,
      { request: contractId },
    );
  }

  registerBankAccount(data: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Cash/AddWithdrawalAccount`, data)
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getBankAccountStatus(contractId: string): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/Cash/GetWithdrawalAccount`,

        {
          ContractId: contractId,
          BusinessUnitParty: this.BUSINESS_PARTY,
        },
      )
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  deleteBankAccount(contractId: string, account: string): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Cash/RemoveWithdrawalAccount`, {
        ContractId: contractId,
        BusinessUnitParty: this.BUSINESS_PARTY,
        BankAccountNumber: account,
      })
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getVerificationStatusBySsiId(data: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Cash/GetVerificationStatusBySsiId`, data)
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getStatusByStatusTypeId(statusTypeId: number): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Cash/GetVerificationStatusBySsiId`, statusTypeId)
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getPartyId(loremUrl: string): Observable<any> {
    this.loremAPI = this.loremAPI === undefined ? loremUrl : this.loremAPI;

    return this.apiGatewayService.get(`${this.loremAPI}users/party`).pipe(
      catchError((error: any) => {
        return observableOf(error);
      }),
    );
  }

  getAccountManagerElegibility(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmComAPI}v1/account-manager-eligibility`)
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  sendDocumentLorem(data: any, partyId: string): Observable<any> {
    return this.apiGatewayService
      .post(`${this.loremAPI}parties/` + partyId + '/documents', data)
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getDocumentLoremAccountEdo(partyId: string): Observable<any> {
    return this.apiGatewayService
      .get(`${this.loremAPI}parties/` + partyId + '/documents')
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  getBanksInformation(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Cash/GetBanks`)
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getWithdrawalAmount(
    contractId: string,
  ): Observable<WithdrawalAmountResponse> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/Operation/GetWithdrawalAmount`, { contractId })
      .pipe(
        catchError((error: any) => {
          return observableOf(error);
        }),
      );
  }

  /// account manager
  getWithdrawalAccounts(
    contractId: string,
  ): Observable<BankAccountViewModel[]> {
    const size = 10;
    const page = 1;
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/withdrawal-accounts?legacy_contract_id=${contractId}&page_size=${size}&page=${page}`,
      )
      .pipe(
        map((account: any) => {
          return !isEmpty(account)
            ? account.items.map((item: any) =>
                BankAccountViewModel.fromJSON(item),
              )
            : [];
        }),
        catchError((error: any) => this.handleError(error.error).pipe(take(1))),
      );
  }

  addWithdrawalAccount(withdrawalData: any): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmComAPI}v1/withdrawal-accounts`, withdrawalData)
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  addWithdrawalAccountWithMFA(
    withdrawalData: any,
    mfaCode: string,
  ): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmComAPI}v2/withdrawal-accounts`, {
        ...withdrawalData,
        code: mfaCode,
      })
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  deleteWithdrawalAccount(
    contractId: string,
    withdrawalAccountId: string,
  ): Observable<any> {
    return this.apiGatewayService
      .delete(
        `${this.gbmComAPI}v1/withdrawal-accounts/${withdrawalAccountId}?legacy_contract_id=${contractId}`,
      )
      .pipe(
        catchError((error: any) => this.handleError(error.error).pipe(take(1))),
      );
  }

  getAgreementLog(agreementTypeId: number): Observable<any> {
    return this.apiGatewayService
      .post(`${this.gbmAPI}api/appmanagement/GetAgreementLog`, {
        agreementTypeId,
      })
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  addAgreementLog(
    agreementTypeId: number,
    contracts: string[],
  ): Observable<any> {
    const agreementStatusTypeId = 1;
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/appmanagement/AddApplicationAgreementLogForContracts`,
        {
          agreementTypeId,
          contracts,
          agreementStatusTypeId,
        },
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  addDocumentWithdrawalAccount(
    withdrawalAccountId: string,
    fileName: string,
    contractId: string,
    fileContent: string,
  ): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmComAPI}v1/withdrawal-accounts/${withdrawalAccountId}/documents/${fileName}`,
        {
          legacy_contract_id: contractId,
          file_content: fileContent,
        },
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getDocumentStatusWithdrawalAccounts(
    withdrawalAccountId: string,
  ): Observable<any> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/withdrawal-accounts/${withdrawalAccountId}/documents`,
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getAvailableTransferAmount(
    contractsId: string,
    accountsId: string,
  ): Observable<any> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v2/contracts/${contractsId}/accounts/${accountsId}`,
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  makeTransfer(
    contractsId: string,
    accountsId: string,
    body: {
      amount: number;
      target_account_id: string;
    },
  ): Observable<{
    id: string;
    amount: number;
    status: string;
    target_account_id: string;
    created_at: string;
  }> {
    return (
      this.apiGatewayService.post(
        `${this.gbmComAPI}v1/contracts/${contractsId}/accounts/${accountsId}/transfers`,
        body,
      ) as unknown as Observable<{
        id: string;
        amount: number;
        status: string;
        target_account_id: string;
        created_at: string;
      }>
    ).pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getTransfers(
    contractsId: string,
    accountsId: string,
  ): Observable<{
    items: Array<{
      id: string;
      amount: number;
      status: string;
      target_account_id: string;
      created_at: string;
    }>;
    pagination_metadata: any;
  }> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/contracts/${contractsId}/accounts/${accountsId}/transfers`,
      )
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  getCashTransactionDetail(
    contractId: string,
    processData: string,
    orderId: number,
  ) {
    const url = `${this.gbmComAPI}v1/legacy-contracts/${contractId}/cash-transaction-details?process_date=${processData}&order_id=${orderId}`;
    return this.apiGatewayService.get(url);
  }

  getTransferDetail(
    contractId: string,
    accountId: string,
    orderId: string,
    orderType: string,
  ) {
    return this.apiGatewayService.get(
      `${this.gbmComAPI}v1/contracts/${contractId}/accounts/${accountId}/transfers/orders/${orderId}?order_type=${orderType}`,
    );
  }

  cancelTransfer(contractId: string, accountId: string, transferId: string) {
    const body = {
      account_id: accountId,
      contract_id: contractId,
    };
    return this.apiGatewayService.post(
      `${this.gbmComAPI}v1/transfers/${transferId}/cancellation`,
      body,
    );
  }

  getDayTrades(
    contractsId: string,
    accountsId: string,
  ): Observable<DayTradesViewModel> {
    return this.apiGatewayService
      .get<IDayTradesResponse>(
        `${this.tradingUsaAPI}v2/orders/contracts/${contractsId}/accounts/${accountsId}/daytrades`,
      )
      .pipe(
        map((daytrades) =>
          !isEmpty(daytrades)
            ? DayTradesViewModel.fromJSON(daytrades)
            : ([] as any),
        ),
        catchError((error) => this.handleError(error)),
        tap((dayTrades) => {
          this.store.dispatch({
            type: DAY_TRADES,
            payload: dayTrades,
          });
          return EMPTY;
        }),
      );
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);

    return observableThrowError(error);
  }

  private getCurrentComContractAndAccount() {
    return combineLatest(
      this.store.select(selectContractId),
      this.store.select(selectContractsAccountsAccounts),
    ).pipe(
      filter(
        ([contractId, accounts]) =>
          !!contractId && !!accounts && accounts.hasOwnProperty(contractId),
      ),
      map(([contractId, accounts]) => accounts[contractId]),
      first(),
    );
  }

  private isBuyByCapitalOrderType(order: {
    capitalOrderTypeId: number;
  }): boolean {
    return buyOrderTypes.includes(order.capitalOrderTypeId);
  }
}
